import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
    User, 
    Mail, 
    Building2, 
    Wrench,
    Settings,
    AlertTriangle,
    Trash2,
    Power,
    Loader2,
    Shield,
    ShieldCheck,
    Star,
    ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { formatDate, cn } from '../lib/utils';

// Verification tier badge
const VerificationBadge = ({ tier }) => {
    const tiers = {
        basic: { label: 'Basic', color: 'bg-gray-500/15 text-gray-600 dark:text-gray-400', icon: Shield },
        verified: { label: 'Verified', color: 'bg-blue-500/15 text-blue-600 dark:text-blue-400', icon: ShieldCheck },
        trusted: { label: 'Trusted', color: 'bg-amber-500/15 text-amber-600 dark:text-amber-400', icon: Star },
    };
    const t = tiers[tier] || tiers.basic;
    const Icon = t.icon;
    return (
        <span className={cn("inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full", t.color)}>
            <Icon className="h-4 w-4" />
            {t.label}
        </span>
    );
};

const AccountSettings = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    
    const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');
    const [loading, setLoading] = useState(false);

    const handleDeactivateAccount = async () => {
        setLoading(true);
        try {
            await api.post('/account/deactivate', { confirm: true });
            toast.success('Account deactivated successfully');
            logout();
            navigate('/');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to deactivate account');
        } finally {
            setLoading(false);
            setDeactivateDialogOpen(false);
        }
    };

    const handleRequestDeletion = async () => {
        setLoading(true);
        try {
            await api.post('/account/request-deletion', { 
                confirm: true, 
                reason: deleteReason 
            });
            toast.success('Deletion request submitted');
            logout();
            navigate('/');
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to submit deletion request');
        } finally {
            setLoading(false);
            setDeleteDialogOpen(false);
        }
    };

    return (
        <Layout>
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex items-center gap-3 mb-8">
                    <Settings className="h-8 w-8 text-primary" />
                    <h1 className="font-heading font-extrabold text-3xl text-foreground">
                        Account Settings
                    </h1>
                </div>

                {/* Profile Information */}
                <Card className="bg-card border-border mb-6">
                    <CardHeader>
                        <CardTitle className="font-heading font-bold text-xl text-foreground flex items-center gap-2">
                            <User className="h-5 w-5 text-primary" />
                            Profile Information
                        </CardTitle>
                        <CardDescription>
                            Your account details
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label className="text-muted-foreground flex items-center gap-2">
                                    <Mail className="h-4 w-4" /> Email
                                </Label>
                                <p className="font-medium text-foreground">{user?.email}</p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-muted-foreground flex items-center gap-2">
                                    <Building2 className="h-4 w-4" /> Company
                                </Label>
                                <p className="font-medium text-foreground">{user?.company_name}</p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-muted-foreground flex items-center gap-2">
                                    <Wrench className="h-4 w-4" /> Trade
                                </Label>
                                <p className="font-medium text-foreground">{user?.contractor_trade}</p>
                            </div>
                            <div className="space-y-2">
                                <Label className="text-muted-foreground flex items-center gap-2">
                                    <Shield className="h-4 w-4" /> Account Status
                                </Label>
                                <p className="font-medium text-emerald-500">Active</p>
                            </div>
                        </div>
                        <div className="pt-4 border-t border-border">
                            <p className="text-sm text-muted-foreground">
                                Member since {formatDate(user?.created_at)}
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Verification Status */}
                <Card className="bg-card border-border mb-6">
                    <CardHeader>
                        <CardTitle className="font-heading font-bold text-xl text-foreground flex items-center gap-2">
                            <ShieldCheck className="h-5 w-5 text-primary" />
                            Verification Status
                        </CardTitle>
                        <CardDescription>
                            Verify your credentials to increase your review influence
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-4">
                                <VerificationBadge tier={user?.verification_tier || 'basic'} />
                                <div>
                                    <p className="text-sm text-muted-foreground">
                                        Review weight: <span className="font-medium text-foreground">{(user?.verification_weight_multiplier || 0.9).toFixed(2)}x</span>
                                    </p>
                                </div>
                            </div>
                            <Button 
                                variant="outline"
                                onClick={() => navigate('/settings/verification')}
                                data-testid="verification-settings-btn"
                            >
                                Manage
                                <ChevronRight className="h-4 w-4 ml-1" />
                            </Button>
                        </div>
                        {user?.verification_tier === 'basic' && (
                            <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                                Upload your license or insurance to get verified and increase your review influence.
                            </p>
                        )}
                    </CardContent>
                </Card>

                {/* Account Controls */}
                <Card className="bg-card border-border">
                    <CardHeader>
                        <CardTitle className="font-heading font-bold text-xl text-foreground flex items-center gap-2">
                            <AlertTriangle className="h-5 w-5 text-amber-500" />
                            Account Controls
                        </CardTitle>
                        <CardDescription>
                            Manage your account status
                        </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Deactivate Account */}
                        <div className="p-4 border border-border rounded-lg">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Power className="h-5 w-5 text-amber-500" />
                                        <h3 className="font-semibold text-foreground">Deactivate Account</h3>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        This disables your account but preserves platform records for moderation, 
                                        audit, and system integrity purposes. You can reactivate later by logging in again.
                                    </p>
                                </div>
                                <Button 
                                    variant="outline" 
                                    className="border-amber-500 text-amber-500 hover:bg-amber-500/10"
                                    onClick={() => setDeactivateDialogOpen(true)}
                                    data-testid="deactivate-account-btn"
                                >
                                    Deactivate
                                </Button>
                            </div>
                        </div>

                        {/* Request Account Deletion */}
                        <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/5">
                            <div className="flex items-start justify-between gap-4">
                                <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-2">
                                        <Trash2 className="h-5 w-5 text-destructive" />
                                        <h3 className="font-semibold text-foreground">Request Account Deletion</h3>
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Submit a request to delete your account. Your account will be deactivated immediately 
                                        and an administrator will review your request. Deletion requests may require retention 
                                        of certain records for moderation, audit, compliance, and system integrity purposes.
                                    </p>
                                </div>
                                <Button 
                                    variant="destructive"
                                    onClick={() => setDeleteDialogOpen(true)}
                                    data-testid="request-deletion-btn"
                                >
                                    Request Deletion
                                </Button>
                            </div>
                        </div>

                        {/* Info Box */}
                        <div className="p-4 bg-accent/30 rounded-lg">
                            <p className="text-xs text-muted-foreground">
                                <strong>Note:</strong> Your reviews, moderation history, and audit records are maintained 
                                for platform integrity regardless of account status. This ensures fair treatment of all 
                                users and protects the contractor community.
                            </p>
                        </div>
                    </CardContent>
                </Card>

                {/* Deactivate Confirmation Dialog */}
                <AlertDialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2">
                                <Power className="h-5 w-5 text-amber-500" />
                                Deactivate Your Account?
                            </AlertDialogTitle>
                            <AlertDialogDescription className="space-y-2">
                                <p>This will:</p>
                                <ul className="list-disc list-inside text-sm space-y-1">
                                    <li>Disable your account and sign you out</li>
                                    <li>Prevent login until you reactivate</li>
                                    <li>Preserve your reviews and platform history</li>
                                </ul>
                                <p className="mt-2">
                                    You can reactivate your account anytime by logging in with your credentials.
                                </p>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleDeactivateAccount}
                                disabled={loading}
                                className="bg-amber-500 hover:bg-amber-600"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Deactivating...
                                    </>
                                ) : (
                                    'Deactivate Account'
                                )}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>

                {/* Delete Request Confirmation Dialog */}
                <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle className="flex items-center gap-2 text-destructive">
                                <Trash2 className="h-5 w-5" />
                                Request Account Deletion?
                            </AlertDialogTitle>
                            <AlertDialogDescription className="space-y-3">
                                <p>This will:</p>
                                <ul className="list-disc list-inside text-sm space-y-1">
                                    <li>Immediately deactivate your account</li>
                                    <li>Submit a deletion request for admin review</li>
                                    <li>Sign you out of the platform</li>
                                </ul>
                                <p className="text-sm">
                                    <strong>Important:</strong> Some records may be retained for moderation, audit, 
                                    compliance, and system integrity purposes per our policies.
                                </p>
                                <div className="pt-2">
                                    <Label htmlFor="deleteReason" className="text-foreground">
                                        Reason for deletion (optional)
                                    </Label>
                                    <Textarea
                                        id="deleteReason"
                                        value={deleteReason}
                                        onChange={(e) => setDeleteReason(e.target.value)}
                                        placeholder="Help us understand why you're leaving..."
                                        className="mt-2"
                                        rows={3}
                                    />
                                </div>
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel disabled={loading}>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                                onClick={handleRequestDeletion}
                                disabled={loading}
                                className="bg-destructive hover:bg-destructive/90"
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                        Submitting...
                                    </>
                                ) : (
                                    'Request Deletion'
                                )}
                            </AlertDialogAction>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </div>
        </Layout>
    );
};

export default AccountSettings;
